//@ts-check
import {Button} from '@/components/atoms'
import {useProductCombo} from '@/context/ProductCombo/ProductComboContext'
import {useFeatureFlagLanguages} from '@/hooks/useFeatureFlagLanguages'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {ProductComboSteps} from '@/types/productComboTypes'
import {ROUTES} from '@/utils/constants/routesConstants'
import {validateProductComboData} from '@/utils/productCombo/productComboValidation'
import {useQueryClient} from '@tanstack/react-query'
import {Font} from 'pepsico-ds'
import {useDispatch} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import {useIsComboNameUnique} from './useIsComboNameUnique'
import {useSaveProductCombo} from './useSaveProductCombo'

const COMBO_LIST_TAB_INDEX = 1

export const useProductComboFooter = () => {
  const {setCurrentStep, currentStep, productCombo, updateProductCombo} = useProductCombo()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isNameUnique = useIsComboNameUnique(productCombo.name, productCombo.productId)
  const saveComboMutation = useSaveProductCombo()
  const languages = useFeatureFlagLanguages()

  const syncProductIdFromSaveResponse = response => {
    const responseProductId = response?.message?.match(/productId=([^\s]+)/)?.[1]

    if (responseProductId && responseProductId !== productCombo.productId) {
      updateProductCombo({productId: responseProductId})
    }

    return response
  }

  const getSaveErrorMessage = error => {
    if (error && typeof error === 'object' && 'data' in error) {
      return error.data?.message
    }

    return undefined
  }

  /**
   * Shows a toast notification.
   * @param {string | import('react').ReactNode} message - Notification message.
   * @param {'success' | 'error'} type - Notification type.
   */
  const showNotification = (message, type) => {
    dispatch(
      setToastNotifications({
        show: true,
        message,
        type,
      })
    )
  }

  const isReview = currentStep === ProductComboSteps.REVIEW

  const moveToNextStep = () => {
    setCurrentStep(prevStep => prevStep + 1)
  }

  const save = async isDraft => {
    const response = await saveComboMutation.mutateAsync({...productCombo, isDraft})
    queryClient.removeQueries({queryKey: ['COMBO_DETAIL']})
    return syncProductIdFromSaveResponse(response)
  }

  const saveDraft = () => save(true)

  const savePublishedCombo = () => save(false)

  /**
   * @param {string | import('react').ReactNode} [message='Product combo saved successfully.']
   */
  const showSaveSuccessNotification = (message = 'Product combo saved successfully.') => {
    showNotification(message, 'success')
  }

  const showSaveErrorNotification = message => {
    showNotification(`Error saving product combination. ${message || 'Please try again.'}`, 'error')
  }

  const handleDraftAction = async () => {
    try {
      await saveDraft()
      showSaveSuccessNotification()
      navigate(ROUTES.PRODUCTS_LIST, {state: {selectedTabNumb: COMBO_LIST_TAB_INDEX}})
    } catch (error) {
      showSaveErrorNotification(getSaveErrorMessage(error))
    }
  }

  const handleNextAction = async () => {
    if (currentStep === ProductComboSteps.SETUP) {
      try {
        if (!productCombo.productId) {
          await saveDraft()
        }
        moveToNextStep()
      } catch (error) {
        showSaveErrorNotification(getSaveErrorMessage(error))
        return
      }
      return
    }

    if (currentStep === ProductComboSteps.REVIEW) {
      try {
        const isUpdate = !productCombo.isDraft
        await savePublishedCombo()
        showSaveSuccessNotification(
          <div className="flex w-full items-center justify-between">
            <div className="flex flex-col">
              <Font className="text-lg" headingLevel={6}>
                {isUpdate ? 'Combo product updated successfully' : 'Combo product created successfully'}
              </Font>
              <Font>Do you want to create a promotion with the new combo: {productCombo.name} ? </Font>
            </div>
            <Button
              handleButton={() => navigate('/products/create-promotion')}
              uiSchema={{size: 'medium', title: 'Create Promotion'}}
            />
          </div>
        )
        navigate(ROUTES.PRODUCTS_LIST, {state: {selectedTabNumb: COMBO_LIST_TAB_INDEX}})
      } catch (error) {
        showSaveErrorNotification(getSaveErrorMessage(error))
      }
      return
    }

    moveToNextStep()
  }

  const handleCancelAction = async () => {
    navigate(ROUTES.PRODUCTS_LIST, {state: {selectedTabNumb: COMBO_LIST_TAB_INDEX}})
  }

  const hasValidationErrors = validateProductComboData(productCombo, currentStep, isNameUnique, languages)

  const hideSaveDraft =
    !productCombo.name || productCombo.name.trim() === '' || !isNameUnique || productCombo.isDraft === false

  return {
    handleNextAction,
    handleDraftAction,
    handleCancelAction,
    isReview,
    hasValidationErrors,
    hideSaveDraft,
    isLoading: saveComboMutation.isPending,
  }
}
